import "react-datepicker/dist/react-datepicker.min.css"
import "./DatePicker.css"

import { useField } from "formik"
import { Calendar } from "lucide-react"
import moment from "moment"
import { useEffect, useState } from "react"
import ReactDatePicker from "react-datepicker"

import Error from "@/components/Error/Error"

import { useTranslation } from "react-i18next"
import DateCustomHeader from "../CustomDateComponents/DateCustomHeader"

interface DatePickerProps {
  label: string
  id: string
  type: string
  minDate?: Date
  className?: string
  name: string
  clear?: boolean
  placeholder?: string
  error?: React.ReactNode
  backError?: string
  disabled?: boolean
  openToDate?: Date
}

export default function DatePicker({
  label,
  id,
  type,
  minDate,
  className,
  name,
  clear,
  placeholder,
  error,
  backError,
  disabled,
  openToDate,
}: DatePickerProps) {
  const [field, , helpers] = useField(name)
  const { value } = field
  const { setValue } = helpers

  function parseInitialDate(value: string | Date, type: string) {
    if (type === "time" && typeof value === "string") {
      const today = moment().format("YYYY-MM-DD")
      return new Date(`${today}T${value}`)
    }
    return new Date(value)
  }
  const [currDate, setCurrDate] = useState(() =>
    value ? parseInitialDate(value, type) : null
  )
  const [isOpen, setIsOpen] = useState(false) // Controls the date picker menu

  function changeCurrDate(newDate: Date) {
    setCurrDate(newDate)
    setValue(getFormattedDate(newDate, type))
  }

  useEffect(() => {
    if (value && !currDate) {
      setCurrDate(new Date(value))
    }
  }, [value])

  const { t } = useTranslation()

  return (
    <div
      className={`position-relative d-flex flex-column gap-2 ${className || ""}`}
    >
      <div className="d-flex justify-content-between">
        <label htmlFor={id} className="fw-bold">
          {label}
        </label>
        {clear && (
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              if (currDate) {
                setValue("")
                setCurrDate(null)
              }
            }}
            disabled={disabled}
          >
            {t("buttons.clear")}
          </button>
        )}
      </div>

      {/* Clickable Wrapper for Input & Icon */}
      <div
        className="position-relative"
        onClick={() => !disabled && setIsOpen(true)}
      >
        <ReactDatePicker
          showTimeSelect={type === "datetime" || type === "time"}
          showTimeSelectOnly={type === "time"}
          showMonthYearPicker={type === "month"}
          selected={currDate}
          id={id}
          minDate={minDate}
          autoComplete="off"
          locale="en-US"
          openToDate={openToDate}
          placeholderText={placeholder}
          dateFormat={
            type === "datetime"
              ? "dd / MM / yyyy hh:mm aa"
              : type === "time"
                ? "hh:mm aa"
                : type === "month"
                  ? "MM / yyyy"
                  : "dd / MM / yyyy"
          }
          onChange={changeCurrDate}
          renderCustomHeader={(props) => <DateCustomHeader {...props} />}
          disabled={disabled}
          open={isOpen} // Controls open state
          onClickOutside={() => setIsOpen(false)} // Close on outside click
          onInputClick={() => setIsOpen(true)} // Open on input click
        />

        {/* Calendar Icon (Clicking it also opens the date picker) */}
        {!disabled && (
          <Calendar
            className="date__icon"
            style={{
              cursor: "pointer",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
            size={20}
          />
        )}
      </div>

      {<Error>{backError || error}</Error>}
    </div>
  )
}

function getFormattedDate(date: Date, type: string) {
  if (type === "datetime") {
    return moment(date).format("YYYY-MM-DD HH:mm:ss")
  } else if (type === "time") {
    return moment(date).format("HH:mm")
  } else {
    return moment(date).format("YYYY-MM-DD")
  }
}
